import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Book, Lock, Search, Sword, Shield, ShieldCheck, ShieldOff, Shirt, HardHat, Link, Feather, Diamond, Shell, Flame, Snowflake, Zap, Bug, Eclipse, Hammer, Target, Skull, Swords, Hand, Circle, Gem, Eye, Footprints, Wallet, FlaskConical, Music, Crown, Ghost, Dice5, Leaf, Clock, Sparkles, Sun, CloudLightning, Compass, Axe, Anchor } from "lucide-react";
import { ALL_ITEMS, ITEM_CATEGORIES, RARITY_COLORS, RARITY_ORDER } from "@/config/items";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'fruitCrush_token';

// Map icon string names to Lucide components
const ICON_MAP = {
  Sword, Shield, ShieldCheck, ShieldOff, Shirt, HardHat, Link, Feather, Diamond, Shell,
  Flame, Snowflake, Zap, Bug, Eclipse, Hammer, Target, Skull, Swords, Hand, Circle,
  Gem, Eye, Footprints, Wallet, FlaskConical, Music, Crown, Ghost, Dice5, Leaf,
  Clock, Sparkles, Sun, CloudLightning, Compass, Axe, Anchor, Search,
};

function ItemIcon({ item, discovered, size = "text-3xl" }) {
  const isEquipment = ['weapon', 'armor', 'accessory'].includes(item.type);
  
  if (isEquipment && item.icon) {
    const LucideIcon = ICON_MAP[item.icon];
    if (LucideIcon) {
      const rarity = RARITY_COLORS[item.rarity] || RARITY_COLORS.common;
      return (
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
          discovered 
            ? `${rarity.bg} border ${rarity.border}` 
            : 'bg-slate-800/50 border border-slate-700/30'
        }`}>
          <LucideIcon className={`w-5 h-5 ${discovered ? rarity.text : 'text-slate-600'}`} />
        </div>
      );
    }
  }
  
  // Emoji fallback for consumables and collectibles
  return (
    <span className={`${size} ${discovered ? '' : 'grayscale opacity-30 blur-[2px]'}`}>
      {discovered ? (item.emoji || '?') : '?'}
    </span>
  );
}

export default function CodexPage() {
  const navigate = useNavigate();
  const [inventory, setInventory] = useState(null);
  const [category, setCategory] = useState('all');
  const [rarityFilter, setRarityFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDiscovered, setShowDiscovered] = useState('all'); // all, discovered, undiscovered

  useEffect(() => {
    const fetchInventory = async () => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) return;
      try {
        const res = await fetch(`${API_URL}/api/inventory/all`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setInventory(data);
        }
      } catch (e) { /* silent */ }
    };
    fetchInventory();
  }, []);

  const discoveredIds = useMemo(() => {
    if (!inventory) return new Set();
    const ids = new Set();
    // Use inventory_raw from API (dict of item_id: count)
    const raw = inventory.inventory_raw || {};
    Object.entries(raw).forEach(([id, count]) => {
      if (count > 0) ids.add(id);
    });
    // Also check category items
    if (inventory.categories) {
      Object.values(inventory.categories).forEach(cat => {
        (cat.items || []).forEach(i => {
          if ((i.owned || 0) > 0) ids.add(i.item_id || i.id);
        });
      });
    }
    return ids;
  }, [inventory]);

  const filteredItems = useMemo(() => {
    return ALL_ITEMS.filter(item => {
      // Category filter
      if (category !== 'all') {
        if (category === 'weapon' || category === 'armor' || category === 'accessory' || category === 'consumable') {
          if (item.type !== category) return false;
        } else {
          // Subtype filter for collectibles
          if (item.subtype !== category && item.type !== category) return false;
        }
      }
      // Rarity filter
      if (rarityFilter !== 'all' && item.rarity !== rarityFilter) return false;
      // Discovery filter
      if (showDiscovered === 'discovered' && !discoveredIds.has(item.id)) return false;
      if (showDiscovered === 'undiscovered' && discoveredIds.has(item.id)) return false;
      // Search
      if (search && !item.name.toLowerCase().includes(search.toLowerCase()) && !item.description.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [category, rarityFilter, search, discoveredIds, showDiscovered]);

  const discoveredCount = ALL_ITEMS.filter(i => discoveredIds.has(i.id)).length;
  const totalItems = ALL_ITEMS.length;
  const pct = Math.round((discoveredCount / totalItems) * 100);

  // Stats per rarity
  const rarityStats = useMemo(() => {
    const stats = {};
    RARITY_ORDER.forEach(r => {
      const total = ALL_ITEMS.filter(i => i.rarity === r).length;
      const found = ALL_ITEMS.filter(i => i.rarity === r && discoveredIds.has(i.id)).length;
      stats[r] = { total, found };
    });
    return stats;
  }, [discoveredIds]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      <div className="container mx-auto px-3 py-4 max-w-2xl">
        {/* Header */}
        <header className="flex items-center justify-between mb-4">
          <Button onClick={() => navigate(-1)} variant="ghost" size="sm" className="text-slate-300 px-2" data-testid="codex-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <h1 className="font-display font-bold text-lg text-amber-300 flex items-center gap-1.5">
            <Book className="w-4 h-4" /> Item Codex
          </h1>
          <div className="text-xs text-slate-400 font-bold tabular-nums">{discoveredCount}/{totalItems}</div>
        </header>

        {/* Overall Progress */}
        <div className="mb-3">
          <div className="h-2.5 bg-slate-800 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-amber-500 to-orange-400 rounded-full transition-all duration-700" style={{ width: `${pct}%` }} />
          </div>
          <div className="flex justify-between mt-1">
            <p className="text-[10px] text-slate-500">{pct}% discovered</p>
            <div className="flex gap-2">
              {RARITY_ORDER.map(r => (
                <span key={r} className={`text-[9px] font-bold ${RARITY_COLORS[r].text}`}>
                  {rarityStats[r]?.found}/{rarityStats[r]?.total}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="flex gap-1 overflow-x-auto pb-2 mb-3 scrollbar-hide" data-testid="codex-categories">
          {ITEM_CATEGORIES.map(cat => (
            <button
              key={cat.id}
              onClick={() => setCategory(cat.id)}
              className={`shrink-0 px-2.5 py-1.5 rounded-lg text-[11px] font-bold transition-all ${
                category === cat.id
                  ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                  : 'bg-slate-800/40 text-slate-400 border border-transparent hover:bg-slate-800/60'
              }`}
              data-testid={`codex-cat-${cat.id}`}
            >
              {cat.label} <span className="opacity-60">{cat.count}</span>
            </button>
          ))}
        </div>

        {/* Search + Filters Row */}
        <div className="flex gap-1.5 mb-3">
          <div className="flex-1 relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              placeholder="Search items..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-8 pr-2 py-1.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-xs text-white placeholder:text-slate-500 focus:outline-none focus:border-amber-500/40"
              data-testid="codex-search"
            />
          </div>
          <select
            value={rarityFilter}
            onChange={e => setRarityFilter(e.target.value)}
            className="px-2 py-1.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-xs text-white focus:outline-none"
            data-testid="codex-rarity-filter"
          >
            <option value="all">All Rarity</option>
            {RARITY_ORDER.map(r => (
              <option key={r} value={r}>{RARITY_COLORS[r].label}</option>
            ))}
          </select>
          <select
            value={showDiscovered}
            onChange={e => setShowDiscovered(e.target.value)}
            className="px-2 py-1.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-xs text-white focus:outline-none"
            data-testid="codex-discovery-filter"
          >
            <option value="all">All</option>
            <option value="discovered">Found</option>
            <option value="undiscovered">Missing</option>
          </select>
        </div>

        {/* Results count */}
        <p className="text-[10px] text-slate-500 mb-2">{filteredItems.length} items</p>

        {/* Item Grid */}
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="codex-grid">
          {filteredItems.map(item => {
            const discovered = discoveredIds.has(item.id);
            const rarity = RARITY_COLORS[item.rarity] || RARITY_COLORS.common;
            const isEquipment = ['weapon', 'armor', 'accessory'].includes(item.type);
            
            return (
              <button
                key={item.id}
                onClick={() => setSelectedItem(item)}
                className={`relative p-2.5 rounded-xl border text-left transition-all hover:scale-[1.02] active:scale-95 ${
                  discovered
                    ? `${rarity.bg} ${rarity.border} ${rarity.glow ? `shadow-lg ${rarity.glow}` : ''}`
                    : 'bg-slate-800/20 border-slate-700/20'
                }`}
                data-testid={`codex-item-${item.id}`}
              >
                <div className="flex flex-col items-center gap-1">
                  <ItemIcon item={item} discovered={discovered} />
                  <p className={`text-[10px] font-bold text-center leading-tight line-clamp-2 ${discovered ? 'text-white' : 'text-slate-600'}`}>
                    {discovered ? item.name : '???'}
                  </p>
                  <div className="flex items-center gap-1">
                    <span className={`text-[8px] font-bold px-1 py-0.5 rounded ${rarity.bg} ${rarity.text}`}>
                      {rarity.label}
                    </span>
                    {isEquipment && discovered && (
                      <span className="text-[8px] font-bold px-1 py-0.5 rounded bg-cyan-900/30 text-cyan-400">
                        EQ
                      </span>
                    )}
                  </div>
                </div>
                {!discovered && (
                  <div className="absolute top-1 right-1">
                    <Lock className="w-2.5 h-2.5 text-slate-600" />
                  </div>
                )}
                {discovered && item.value >= 5000 && (
                  <div className="absolute top-1 right-1 text-[8px]">
                    <span className="text-amber-400">★</span>
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {filteredItems.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            <Book className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No items match your filters.</p>
          </div>
        )}

        {/* Item Detail Modal */}
        {selectedItem && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setSelectedItem(null)} data-testid="codex-detail-modal">
            <div className={`max-w-sm w-full rounded-2xl border p-5 space-y-3 ${RARITY_COLORS[selectedItem.rarity]?.bg || 'bg-slate-800'} ${RARITY_COLORS[selectedItem.rarity]?.border || 'border-slate-700'}`} onClick={e => e.stopPropagation()}
              style={{ animation: 'codexModalIn 0.3s ease-out' }}>
              {(() => {
                const discovered = discoveredIds.has(selectedItem.id);
                const rarity = RARITY_COLORS[selectedItem.rarity] || RARITY_COLORS.common;
                const isEquipment = ['weapon', 'armor', 'accessory'].includes(selectedItem.type);
                
                return (
                  <>
                    <div className="text-center">
                      <div className="flex justify-center mb-2">
                        <div className="scale-150">
                          <ItemIcon item={selectedItem} discovered={discovered} size="text-5xl" />
                        </div>
                      </div>
                      <h3 className="font-display font-bold text-lg text-white mt-3">
                        {discovered ? selectedItem.name : '???'}
                      </h3>
                      <div className="flex items-center justify-center gap-1.5 mt-1">
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${rarity.bg} ${rarity.text}`}>
                          {rarity.label}
                        </span>
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-700/50 text-slate-300">
                          {selectedItem.type === 'collectible' ? (selectedItem.subtype || 'Collectible') : selectedItem.type}
                        </span>
                        {isEquipment && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-cyan-900/30 text-cyan-400">
                            Equippable
                          </span>
                        )}
                      </div>
                    </div>

                    {discovered ? (
                      <>
                        <p className="text-slate-300 text-sm text-center leading-relaxed">{selectedItem.description}</p>
                        
                        {/* Equipment effects */}
                        {isEquipment && selectedItem.effect && (
                          <div className="bg-slate-800/50 rounded-lg p-3 space-y-1">
                            <p className="text-[10px] font-bold text-cyan-400 uppercase tracking-wider">Effects</p>
                            {Object.entries(selectedItem.effect).map(([key, val]) => (
                              <div key={key} className="flex justify-between text-xs">
                                <span className="text-slate-400">{key.replace(/_/g, ' ')}</span>
                                <span className="text-white font-bold">
                                  {typeof val === 'boolean' ? (val ? 'Yes' : 'No') : typeof val === 'number' && val < 1 && val > 0 ? `+${Math.round(val * 100)}%` : `${val}`}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="flex justify-between items-center bg-slate-800/50 rounded-lg p-3">
                          <div>
                            <p className="text-[10px] text-slate-500">Source</p>
                            <p className="text-xs text-amber-400 font-bold">{selectedItem.source}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-[10px] text-slate-500">Value</p>
                            <p className="text-xs text-yellow-400 font-bold">{selectedItem.value?.toLocaleString()} coins</p>
                          </div>
                        </div>
                      </>
                    ) : (
                      <p className="text-slate-500 text-sm text-center italic py-2">Discover this item to reveal its secrets...</p>
                    )}

                    <button onClick={() => setSelectedItem(null)} className="w-full py-2 bg-slate-700/50 hover:bg-slate-700 rounded-lg text-slate-300 text-xs font-bold transition-colors">
                      Close
                    </button>
                  </>
                );
              })()}
            </div>
            <style>{`
              @keyframes codexModalIn {
                0% { opacity: 0; transform: scale(0.95) translateY(10px); }
                100% { opacity: 1; transform: scale(1) translateY(0); }
              }
            `}</style>
          </div>
        )}
      </div>
    </div>
  );
}
